import { FC, useMemo } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import {
  giftDeliveryCategoryListDataAtom,
  giftDeliveryCategoryListStatusAtom,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import { useGiftDeliveryCategoryListHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import {
  giftItemInfoBrandFilterAtom,
  giftItemInfoDeliveryCategoryFilterAtom,
  giftItemInfoListDataAtom,
  giftItemInfoListStatusAtom,
  giftItemInfoSearchQueryAtom,
  GiftItemInfoSortType,
  giftItemInfoSortTypeAtom,
  GiftItemInfoStatusFilter,
  giftItemInfoStatusFilterAtom,
  GiftItemInfoTaxFilter,
  giftItemInfoTaxFilterAtom,
  GiftItemInfoTypeFilter,
  giftItemInfoTypeFilterAtom,
} from '@shared/api/giftItemInfo/giftItemInfo.atom';
import { useGiftItemInfoListHook } from '@shared/api/giftItemInfo/giftItemInfo.hook';
import {
  GiftItemInfoData,
  GiftItemInfoType,
} from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { Link } from 'react-router-dom';

export const GiftItemInfoListPage: FC = function GiftItemInfoListPage() {
  const { sellerRole } = useAuth();

  useGiftItemInfoListHook(sellerRole());
  useGiftItemBrandListHook(sellerRole());
  useGiftDeliveryCategoryListHook(sellerRole());

  const status = useRecoilValue(giftItemInfoListStatusAtom);
  const data = useRecoilValue(giftItemInfoListDataAtom);

  // 브랜드 목록
  const brandListStatus = useRecoilValue(giftItemBrandListStatusAtom);
  const brandList = useRecoilValue(giftItemBrandListDataAtom);

  // 배송 카테고리 목록
  const deliveryCategoryListStatus = useRecoilValue(giftDeliveryCategoryListStatusAtom);
  const deliveryCategoryList = useRecoilValue(giftDeliveryCategoryListDataAtom);

  // 필터 및 검색 상태 (Recoil로 관리하여 페이지 이동 후에도 유지)
  const [searchQuery, setSearchQuery] = useRecoilState(giftItemInfoSearchQueryAtom);
  const [typeFilter, setTypeFilter] = useRecoilState(giftItemInfoTypeFilterAtom);
  const [statusFilter, setStatusFilter] = useRecoilState(giftItemInfoStatusFilterAtom);
  const [brandFilter, setBrandFilter] = useRecoilState(giftItemInfoBrandFilterAtom);
  const [deliveryCategoryFilter, setDeliveryCategoryFilter] = useRecoilState(
    giftItemInfoDeliveryCategoryFilterAtom,
  );
  const [taxFilter, setTaxFilter] = useRecoilState(giftItemInfoTaxFilterAtom);
  const [sortType, setSortType] = useRecoilState(giftItemInfoSortTypeAtom);

  // 타입 필터가 배송형이 아닐 때만 배송 카테고리 필터가 의미를 가지므로,
  // 배송형이 아니면 배송 카테고리 필터를 초기화한다.
  const handleTypeFilterChange = (value: GiftItemInfoTypeFilter) => {
    setTypeFilter(value);
    if (value !== 'Delivery') {
      setDeliveryCategoryFilter('all');
    }
  };

  const getImageUrl = (item: GiftItemInfoData): string => {
    const image = item.image;
    if (image && typeof image === 'object' && 'url' in image) {
      return fileBucketUrl((image as Image).url, 'raw');
    }
    return '';
  };

  // 필터링 및 정렬된 데이터
  const filteredAndSortedData = useMemo(() => {
    if (!data) return [];

    let result = [...data];

    // 1. 검색 필터 (이름, 태그, 상품코드)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.tags?.some((tag) => tag.toLowerCase().includes(query)) ||
          (item.productCode != null && String(item.productCode).includes(query)),
      );
    }

    // 2. 타입 필터
    if (typeFilter !== 'all') {
      result = result.filter((item) => item.type === typeFilter);
    }

    // 3. 활성화 상태 필터
    if (statusFilter !== 'all') {
      result = result.filter((item) =>
        statusFilter === 'enabled' ? item.isEnabled : !item.isEnabled,
      );
    }

    // 4. 과세여부 필터
    if (taxFilter !== 'all') {
      result = result.filter((item) =>
        taxFilter === 'taxable' ? item.isTaxable !== false : item.isTaxable === false,
      );
    }

    // 5. 브랜드 필터
    if (brandFilter !== 'all') {
      if (brandFilter === 'none') {
        // 브랜드 없는 상품
        result = result.filter((item) => !item.giftItemBrand);
      } else {
        result = result.filter((item) => {
          const brandId =
            typeof item.giftItemBrand === 'object'
              ? item.giftItemBrand?._id
              : item.giftItemBrand;
          return brandId === brandFilter;
        });
      }
    }

    // 6. 배송 카테고리 필터 (배송형 상품에만 적용)
    if (deliveryCategoryFilter !== 'all') {
      if (deliveryCategoryFilter === 'none') {
        // 배송 카테고리가 지정되지 않은 상품
        result = result.filter(
          (item) =>
            item.type === GiftItemInfoType.Delivery && !item.giftDeliveryCategory,
        );
      } else {
        result = result.filter((item) => {
          if (item.type !== GiftItemInfoType.Delivery) return false;
          const categoryId =
            typeof item.giftDeliveryCategory === 'object'
              ? item.giftDeliveryCategory?._id
              : item.giftDeliveryCategory;
          return categoryId === deliveryCategoryFilter;
        });
      }
    }

    // 7. 정렬
    switch (sortType) {
      case 'order':
        result.sort((a, b) => {
          const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
          const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });
        break;
      case 'name-asc':
        result.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
        break;
      case 'name-desc':
        result.sort((a, b) => b.name.localeCompare(a.name, 'ko'));
        break;
      case 'price-asc':
        result.sort((a, b) => (a.priceWhole || 0) - (b.priceWhole || 0));
        break;
      case 'price-desc':
        result.sort((a, b) => (b.priceWhole || 0) - (a.priceWhole || 0));
        break;
      case 'latest':
        result.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case 'oldest':
        result.sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
    }

    return result;
  }, [
    data,
    searchQuery,
    typeFilter,
    statusFilter,
    taxFilter,
    brandFilter,
    deliveryCategoryFilter,
    sortType,
  ]);

  // 통계 정보
  const stats = useMemo(() => {
    if (!data) return { total: 0, delivery: 0, coupon: 0, enabled: 0, disabled: 0 };
    return {
      total: data.length,
      delivery: data.filter((item) => item.type === GiftItemInfoType.Delivery).length,
      coupon: data.filter((item) => item.type === GiftItemInfoType.Coupon).length,
      enabled: data.filter((item) => item.isEnabled).length,
      disabled: data.filter((item) => !item.isEnabled).length,
    };
  }, [data]);

  const itemListRender = useMemo(() => {
    if (status === 'success') {
      if (filteredAndSortedData.length === 0) {
        return (
          <div className="py-8 text-center text-gray-500">
            {searchQuery ||
            typeFilter !== 'all' ||
            statusFilter !== 'all' ||
            taxFilter !== 'all' ||
            brandFilter !== 'all' ||
            deliveryCategoryFilter !== 'all'
              ? '검색 결과가 없습니다.'
              : '등록된 원상품이 없습니다.'}
          </div>
        );
      }

      return (
        <div className="space-y-2">
          {filteredAndSortedData.map((item) => (
            <Link
              key={item._id}
              to={`/gift-seller/item-info/${item._id}/edit`}
              className="flex items-center gap-3 rounded-lg border bg-white p-3 hover:bg-gray-50"
            >
              <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                {getImageUrl(item) ? (
                  <img
                    src={getImageUrl(item)}
                    alt={item.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                    No Image
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  {/* 상품코드 뱃지 */}
                  {item.productCode != null && (
                    <span className="rounded bg-sky-100 px-1.5 py-0.5 text-xs font-medium text-sky-700">
                      코드: {item.productCode}
                    </span>
                  )}
                  {/* 표시순서 뱃지 */}
                  {item.order !== undefined && (
                    <span className="rounded bg-gray-700 px-1.5 py-0.5 text-xs font-medium text-white">
                      #{item.order}
                    </span>
                  )}
                  {/* 브랜드 뱃지 */}
                  {item.giftItemBrand && typeof item.giftItemBrand === 'object' && (
                    <span className="rounded bg-orange-100 px-1.5 py-0.5 text-xs font-medium text-orange-600">
                      {item.giftItemBrand.name}
                    </span>
                  )}
                  <span
                    className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                      item.type === GiftItemInfoType.Delivery
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-purple-100 text-purple-600'
                    }`}
                  >
                    {item.type === GiftItemInfoType.Delivery ? '배송' : '쿠폰'}
                  </span>
                  {/* 배송 카테고리 뱃지 */}
                  {item.type === GiftItemInfoType.Delivery &&
                    item.giftDeliveryCategory &&
                    typeof item.giftDeliveryCategory === 'object' && (
                      <span className="rounded bg-cyan-100 px-1.5 py-0.5 text-xs font-medium text-cyan-700">
                        {item.giftDeliveryCategory.name}
                      </span>
                    )}
                  <span
                    className={`rounded px-1.5 py-0.5 text-xs ${
                      item.isEnabled
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {item.isEnabled ? '활성' : '비활성'}
                  </span>
                  {/* 선택형 뱃지 */}
                  {item.isSelectable && (
                    <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-xs font-medium text-indigo-600">
                      선택형
                      {item.choiceUnits && item.choiceUnits.length > 0 && (
                        <span className="ml-0.5">({item.choiceUnits.length})</span>
                      )}
                    </span>
                  )}
                  {/* 과세여부 뱃지 */}
                  <span
                    className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                      item.isTaxable !== false
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-emerald-100 text-emerald-700'
                    }`}
                  >
                    {item.isTaxable !== false ? '과세' : '비과세'}
                  </span>
                </div>
                <p className="mt-0.5 truncate font-medium">{item.name}</p>
                <div className="flex flex-wrap items-center gap-x-2 text-sm">
                  {item.priceRegular ? (
                    <span className="text-gray-400 line-through">
                      {item.priceRegular?.toLocaleString()}원
                    </span>
                  ) : null}
                  <span className="font-medium text-red-500">
                    {item.priceRetail?.toLocaleString() || 0}원
                  </span>
                  <span className="text-xs text-gray-400">
                    (도매: {item.priceWhole?.toLocaleString()}원)
                  </span>
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {item.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="rounded-full bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-600"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* 선택유닛 목록 (선택형일 때) */}
              {item.isSelectable && item.choiceUnits && item.choiceUnits.length > 0 && (
                <div className="flex flex-shrink-0 items-center gap-3 border-l pl-4">
                  <span className="text-sm font-medium text-gray-500">선택유닛</span>
                  <div className="flex items-center gap-2">
                    {item.choiceUnits.map((unit) => (
                      <div
                        key={typeof unit === 'object' ? unit._id : unit}
                        className="flex items-center gap-2 rounded-lg bg-indigo-50 py-1 pl-1 pr-3"
                        title={typeof unit === 'object' ? unit.name : ''}
                      >
                        <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                          {typeof unit === 'object' && unit.image ? (
                            <img
                              src={fileBucketUrl(
                                typeof unit.image === 'object'
                                  ? unit.image.url
                                  : unit.image,
                                'raw',
                              )}
                              alt={unit.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                              No
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <span className="max-w-[100px] truncate text-xs font-medium text-gray-700">
                            {typeof unit === 'object' ? unit.name : unit}
                          </span>
                          {/* 가격 표시 */}
                          {typeof unit === 'object' &&
                            (unit.priceRegular || unit.priceWhole) && (
                              <div className="flex items-center gap-1 text-[10px]">
                                {unit.priceRegular ? (
                                  <span className="text-gray-600">
                                    {unit.priceRegular.toLocaleString()}원
                                  </span>
                                ) : null}
                                {unit.priceWhole ? (
                                  <span className="text-gray-400">
                                    (도매 {unit.priceWhole.toLocaleString()})
                                  </span>
                                ) : null}
                              </div>
                            )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-gray-400">→</div>
            </Link>
          ))}
        </div>
      );
    }

    return (
      <div className="center-box h-screen-15">
        <Loading />
      </div>
    );
  }, [
    status,
    filteredAndSortedData,
    searchQuery,
    typeFilter,
    statusFilter,
    taxFilter,
    brandFilter,
    deliveryCategoryFilter,
  ]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">원상품 관리</h1>
          <Link to="/gift-seller/item-info/new">
            <Button className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white">
              + 원상품 등록
            </Button>
          </Link>
        </div>

        {/* 통계 */}
        {status === 'success' && (
          <div className="mt-3 flex gap-4 text-sm">
            <span className="text-gray-600">
              전체 <span className="font-medium text-gray-900">{stats.total}</span>
            </span>
            <span className="text-blue-600">
              배송형 <span className="font-medium">{stats.delivery}</span>
            </span>
            <span className="text-purple-600">
              쿠폰형 <span className="font-medium">{stats.coupon}</span>
            </span>
            <span className="text-green-600">
              활성 <span className="font-medium">{stats.enabled}</span>
            </span>
            <span className="text-gray-500">
              비활성 <span className="font-medium">{stats.disabled}</span>
            </span>
          </div>
        )}
      </div>

      {/* 필터 및 검색 */}
      <div className="border-b bg-white px-6 py-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* 검색 */}
          <div className="relative flex-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="상품명, 태그, 상품코드 검색..."
              className="w-full rounded-lg border px-3 py-2 pl-9 text-sm"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              🔍
            </span>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          {/* 브랜드 필터 */}
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
            disabled={brandListStatus !== 'success'}
          >
            <option value="all">전체 브랜드</option>
            <option value="none">브랜드 없음</option>
            {brandList.map((brand) => (
              <option key={brand._id} value={brand._id}>
                {brand.name}
              </option>
            ))}
          </select>

          {/* 타입 필터 */}
          <select
            value={typeFilter}
            onChange={(e) =>
              handleTypeFilterChange(e.target.value as GiftItemInfoTypeFilter)
            }
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 타입</option>
            <option value="Delivery">배송형</option>
            <option value="Coupon">쿠폰형</option>
          </select>

          {/* 배송 카테고리 필터 (배송형일 때만 노출) */}
          {typeFilter === 'Delivery' && (
            <select
              value={deliveryCategoryFilter}
              onChange={(e) => setDeliveryCategoryFilter(e.target.value)}
              className="rounded-lg border px-3 py-2 text-sm"
              disabled={deliveryCategoryListStatus !== 'success'}
            >
              <option value="all">전체 배송 카테고리</option>
              <option value="none">카테고리 없음</option>
              {deliveryCategoryList.map((category) => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
          )}

          {/* 활성화 필터 */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as GiftItemInfoStatusFilter)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 상태</option>
            <option value="enabled">활성만</option>
            <option value="disabled">비활성만</option>
          </select>

          {/* 과세여부 필터 */}
          <select
            value={taxFilter}
            onChange={(e) => setTaxFilter(e.target.value as GiftItemInfoTaxFilter)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 과세</option>
            <option value="taxable">과세만</option>
            <option value="taxFree">비과세만</option>
          </select>

          {/* 정렬 */}
          <select
            value={sortType}
            onChange={(e) => setSortType(e.target.value as GiftItemInfoSortType)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="order">표시순서</option>
            <option value="latest">최신순</option>
            <option value="oldest">오래된순</option>
            <option value="name-asc">이름 ㄱ→ㅎ</option>
            <option value="name-desc">이름 ㅎ→ㄱ</option>
            <option value="price-asc">가격 낮은순</option>
            <option value="price-desc">가격 높은순</option>
          </select>
        </div>

        {/* 검색 결과 수 */}
        {status === 'success' && (
          <div className="mt-2 text-xs text-gray-500">
            {filteredAndSortedData.length}개 표시
            {(searchQuery ||
              typeFilter !== 'all' ||
              statusFilter !== 'all' ||
              taxFilter !== 'all' ||
              brandFilter !== 'all' ||
              deliveryCategoryFilter !== 'all') &&
              ` (전체 ${stats.total}개 중)`}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">{itemListRender}</div>
    </div>
  );
};
